import { Button } from '@zendeskgarden/react-buttons'
import React, { createRef, useEffect, useState } from 'react'

import BackImage from '../../icons/back.svg?react'
import CheckMarkIcon from '../../icons/checkmark.svg?react'
import ChipCloseIcon from '../../icons/chip_close.svg?react'
import { User } from '../../models/User'
import { splitName } from '../../utils/users'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import { ContactAvatar } from '../contacts/ContactAvatarV1'
import { ThreadHeader } from './ThreadHeader'
import { ConversationState } from './MessagesPage'

interface NewConversationProps {
    conversationState: ConversationState
    inboxHeight: number
    onCreateGroup: (group: User[]) => void
    onSelectUser: (userId: string) => void
    onSearch: (query: string) => void
    onBack: () => void
}

export const SelectRecipientsView: React.FC<NewConversationProps> = ({
    conversationState,
    inboxHeight,
    onCreateGroup,
    onSelectUser,
    onSearch,
    onBack,
}) => {
    const { newConversation } = conversationState
    const { searchText, isSearching, didSearch, searchResults } = newConversation

    const [selectedUsers, setSelectedUsers] = useState(newConversation.selectedUsers)
    const [allowGroup, setAllowGroup] = useState(false)

    const searchInputRef = createRef<HTMLInputElement>()
    const owner = PSApplication.owner()

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [searchInputRef])

    useEffect(() => {
        onSearch('')
    }, [])

    useEffect(() => {
        if (!owner) return
        setAllowGroup(owner.allowGroup || false)
    }, [owner])

    const toggleSelectedUser = (user: User) => {
        setSelectedUsers((prev) => {
            const isSelected = prev.some((u) => u.id === user.id)
            return isSelected ? prev.filter((u) => u.id !== user.id) : [...prev, user]
        })
    }

    const startNewConversation = (userId: string | undefined) => {
        if (!userId) return

        if (allowGroup) {
            const user = searchResults.find((u) => u.id === userId)
            if (user) {
                toggleSelectedUser(user)
            }
        }
        else {
            onSelectUser(userId)
        }
    }

    const handleNextClick = () => {
        if (selectedUsers.length === 1 && selectedUsers[0].id) {
            onSelectUser(selectedUsers[0].id)
        }
        else {
            onCreateGroup(selectedUsers)
        }
    }

    const backLink = (
        <Button
            isLink
            onClick={onBack}
            style={{
                marginTop: '15px',
                marginLeft: '8px',
                lineHeight: '24px',
                fontSize: '14px',
                color: '#3D84F6',
                fontWeight: 700,
                textDecoration: 'none',
            }}
        >
            <span className="flex items-center justify-start">
                <BackImage style={{ marginRight: '4px' }} /> Back
            </span>
        </Button>
    )

    const nextLink = (
        <Button
            isLink
            onClick={handleNextClick}
            disabled={selectedUsers.length === 0}
            style={{
                marginTop: '-8px',
                color: '#3D84F6',
                opacity: selectedUsers.length ? '1' : '0.3',
                fontSize: '14px',
                lineHeight: '24px',
                fontWeight: 700,
                textDecoration: 'none',
            }}
        >
            Next
        </Button>
    )

    const userListHeight = inboxHeight - 77 - 64

    return (
        <>
            <ThreadHeader title={backLink} trailingWidget={allowGroup ? nextLink : null} />
            <div className="newConversation">
                <div className="newConversationChipHolder">
                    {selectedUsers.map((user) => (
                        <div className="chip" key={`new-message-selected-${user.id}`}>
                            {user.name}
                            <ChipCloseIcon
                                className="closeIcon"
                                onClick={() => toggleSelectedUser(user)}
                            />
                        </div>
                    ))}
                    <input
                        type="text"
                        value={searchText}
                        onChange={(e) => onSearch(e.target.value.trimLeft())}
                        placeholder="Who would you like to text?"
                        ref={searchInputRef}
                    />
                </div>
            </div>
            <div className="newConversationUsers" style={{ height: userListHeight }}>
                {isSearching && (
                    <div style={{ textAlign: 'center', padding: '128px 0' }}>
                        <Spinner />
                    </div>
                )}
                {didSearch && !isSearching && searchResults.length === 0 && (
                    <div>No matching clients found</div>
                )}
                {didSearch &&
                    !isSearching &&
                    searchResults.map((searchResult) => {
                        const [firstNamePart, lastNamePart] = splitName(searchResult)
                        const isUserSelected = selectedUsers.some((u) => u.id === searchResult.id)

                        return (
                            <div
                                className="searchResultUser"
                                key={`new-message-result-${searchResult.id}`}
                                onClick={() => startNewConversation(searchResult.id)}
                            >
                                <ContactAvatar user={searchResult} />
                                <span
                                    style={{
                                        marginLeft: '16px',
                                        verticalAlign: 'middle',
                                        color: '#000000',
                                    }}
                                >
                                    {firstNamePart}&nbsp;
                                    <span style={{ fontWeight: 700 }}>{lastNamePart}</span>
                                </span>
                                {allowGroup && (
                                    <div className="checkmark">
                                        {isUserSelected ? (
                                            <CheckMarkIcon />
                                        ) : (
                                            <div className="unselected" />
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
            </div>
        </>
    )
}
